import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import MKBox from 'components/MaterialKit/MKBox';
import { handleErrorResponse } from 'utils/general';
import { withLoading } from 'utils/hoc';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';
import { createBrand, getBrand, updateBrand } from 'api/monitors';
import EditBrandForm from './EditBrandForm';

const EditBrandSection = ({
  setLoading,
  title_color,
  title_font_size,
  title_font_weight,
  edit_button_color,
  edit_font_color,
  save_button_color,
  save_button_font_color,
  back_button_color,
  back_button_font_color,
}) => {
  const [lm, setLm] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getLocaleMap(['labels_edit', 'labels_create'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const [brand, setBrand] = useState(null);
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const brandId = searchParams.get('brand_id');

  const fetchBrandFromApi = useCallback(() => {
    if (!brandId) {
      return Promise.resolve();
    }
    setLoading(true);
    return getBrand(brandId)
      .then(({ data }) => {
        setBrand(data);
      })
      .catch((error) => {
        handleErrorResponse(error, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId, setAuth, setLoading]);

  const updateBrandToApi = useCallback((values, { setFieldError }) => {
    if (!brandId) {
      return Promise.resolve();
    }
    const updateBody = {
      name: values.name,
      display_status: values.display_status,
    };
    setLoading(true);
    return updateBrand(brandId, updateBody)
      .then(({ data }) => {
        setBrand(data);
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [brandId, navigate, setLoading]);

  const createBrandToApi = useCallback((values, { setFieldError }) => {
    const createBody = {
      name: values.name,
      display_status: values.display_status,
    };
    setLoading(true);
    return createBrand(createBody)
      .then(({ data }) => {
        setBrand(data);
        navigate(-1);
      })
      .catch((err) => {
        setFieldError('form', err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, setLoading]);

  useEffect(() => {
    fetchBrandFromApi();
  }, [fetchBrandFromApi]);

  return (
    <MKBox flex={1} py="2rem">
      <Container>
        <div style={{ marginBottom: 10, color: title_color, fontSize: title_font_size, fontWeight: title_font_weight }}>
          {brand ? `Edit Brand [${brand?.name}]` : 'Create Brand'}
        </div>
        <EditBrandForm
          brand={brand}
          onSave={brandId ? updateBrandToApi : createBrandToApi}
          editButtonColor={edit_button_color}
          editFontColor={edit_font_color}
          saveButtonColor={save_button_color}
          saveButtonFontColor={save_button_font_color}
          backButtonColor={back_button_color}
          backButtonFontColor={back_button_font_color}
        />
      </Container>
    </MKBox>
  );
};

EditBrandSection.propTypes = {
  setLoading: PropTypes.func,
  title_color: PropTypes.string,
  title_font_size: PropTypes.string,
  title_font_weight: PropTypes.string,
  edit_button_color: PropTypes.string,
  edit_font_color: PropTypes.string,
  save_button_color: PropTypes.string,
  save_button_font_color: PropTypes.string,
  back_button_color: PropTypes.string,
  back_button_font_color: PropTypes.string,
};

export default withLoading(EditBrandSection);
